/**
 * FLIP reflow for Kanban cards: call `capture()` right before a state change
 * that moves cards; after the next commit, cards slide from their captured
 * position to the new one (transform only). A newly shown placeholder fades in.
 *
 * Elements opt in with `data-kanban-flip="<key>"` inside a
 * `data-kanban-flip-scope="<id>"` scroll container.
 *
 * Reads and writes are batched (cancel all → measure all → animate all) so a
 * reflow costs one layout, not one per card.
 */

import { useCallback, useLayoutEffect, useRef, type RefObject } from 'react';
import {
  KANBAN_DRAG_EASING,
  KANBAN_FLIP_ANIMATION_ID,
  KANBAN_PLACEHOLDER_FLIP_PREFIX,
  KANBAN_REFLOW_MS,
  getFlipDelta,
  prefersReducedMotion,
  type KanbanFlipPosition,
} from './Kanban.motion';

const FLIP_SELECTOR = '[data-kanban-flip]';

function readPositions(root: HTMLElement) {
  const scopeRects = new Map<HTMLElement, DOMRect>();
  const positions: Array<{ element: HTMLElement; position: KanbanFlipPosition }> = [];
  root.querySelectorAll<HTMLElement>(FLIP_SELECTOR).forEach((element) => {
    const scope = element.closest<HTMLElement>('[data-kanban-flip-scope]');
    if (!scope) return;
    let scopeRect = scopeRects.get(scope);
    if (!scopeRect) {
      scopeRect = scope.getBoundingClientRect();
      scopeRects.set(scope, scopeRect);
    }
    const rect = element.getBoundingClientRect();
    positions.push({
      element,
      position: {
        scope: scope.dataset.kanbanFlipScope ?? '',
        x: rect.left - scopeRect.left + scope.scrollLeft,
        y: rect.top - scopeRect.top + scope.scrollTop,
        height: rect.height,
      },
    });
  });
  return positions;
}

/**
 * Where an element was before the change: its own key if it was visible,
 * else its `data-kanban-flip-from` key (the drop slot handing over between
 * the faded source card and the placeholder).
 */
function findPreviousPosition(
  element: HTMLElement,
  before: Map<string, KanbanFlipPosition>
): KanbanFlipPosition | undefined {
  const own = before.get(element.dataset.kanbanFlip ?? '');
  if (own && own.height !== 0) return own;
  const fromKey = element.dataset.kanbanFlipFrom;
  return (fromKey ? before.get(fromKey) : undefined) ?? own;
}

export function useKanbanFlip(rootRef: RefObject<HTMLElement | null>) {
  const snapshotRef = useRef<Map<string, KanbanFlipPosition> | null>(null);
  const runningRef = useRef(new Set<Animation>());

  const capture = useCallback(() => {
    const root = rootRef.current;
    if (!root || prefersReducedMotion()) return;
    // Visual positions (including any running reflow) so interrupted slides continue smoothly.
    const snapshot = new Map<string, KanbanFlipPosition>();
    for (const { element, position } of readPositions(root)) {
      snapshot.set(element.dataset.kanbanFlip ?? '', position);
    }
    snapshotRef.current = snapshot;
  }, [rootRef]);

  useLayoutEffect(() => {
    const before = snapshotRef.current;
    const root = rootRef.current;
    if (!before || !root) return;
    snapshotRef.current = null;

    for (const animation of runningRef.current) animation.cancel();
    runningRef.current.clear();

    const timing = {
      duration: KANBAN_REFLOW_MS,
      easing: KANBAN_DRAG_EASING,
      id: KANBAN_FLIP_ANIMATION_ID,
    };
    const track = (animation: Animation) => {
      runningRef.current.add(animation);
      animation.onfinish = () => runningRef.current.delete(animation);
      animation.oncancel = () => runningRef.current.delete(animation);
    };

    const fadeIns: Array<{ element: HTMLElement; opacity: string }> = [];
    const slides: Array<{ element: HTMLElement; dx: number; dy: number }> = [];
    for (const { element, position: next } of readPositions(root)) {
      const previous = findPreviousPosition(element, before);
      if (!previous || previous.scope !== next.scope) {
        const key = element.dataset.kanbanFlip ?? '';
        if (key.startsWith(KANBAN_PLACEHOLDER_FLIP_PREFIX)) {
          fadeIns.push({ element, opacity: getComputedStyle(element).opacity });
        }
        continue;
      }
      const delta = getFlipDelta(previous, next);
      if (delta) slides.push({ element, ...delta });
    }

    for (const { element, dx, dy } of slides) {
      track(
        element.animate(
          [
            { transform: `translate3d(${dx}px, ${dy}px, 0)` },
            { transform: 'translate3d(0, 0, 0)' },
          ],
          timing
        )
      );
    }
    for (const { element, opacity } of fadeIns) {
      track(element.animate([{ opacity: 0 }, { opacity }], timing));
    }
  });

  return capture;
}
